// src/pages/wallets/WalletsPage.tsx

import React, { useState } from 'react';
import * as api from '../../api/walletApi';
import { WalletResumen } from '../../types';

const fmt = (n?: number) =>
  new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(n ?? 0);

export default function WalletsPage() {
  const [mvzId, setMvzId] = useState('');
  const [wallet, setWallet] = useState<WalletResumen | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [ajusteModal, setAjusteModal] = useState(false);
  const [ajusteMonto, setAjusteMonto] = useState('');
  const [ajusteDesc, setAjusteDesc] = useState('');
  const [ajusteLoading, setAjusteLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  const handleBuscar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mvzId.trim()) return;
    setLoading(true);
    setError('');
    setWallet(null);
    try {
      const res = await api.getWalletMvz(mvzId.trim());
      setWallet(res.content!);
    } catch (err: any) {
      setError(err.response?.data?.mensaje || 'No se encontró wallet para ese MVZ ID');
    } finally {
      setLoading(false);
    }
  };

  const handleAjuste = async () => {
    if (!wallet || !ajusteMonto || !ajusteDesc.trim()) return;
    setAjusteLoading(true);
    try {
      const res = await api.ajusteManual(wallet.mvzId, parseFloat(ajusteMonto), ajusteDesc);
      setWallet(res.content!);
      setAjusteModal(false);
      setAjusteMonto('');
      setAjusteDesc('');
      setSuccessMsg('Ajuste aplicado correctamente');
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.mensaje || 'Error al aplicar ajuste');
    } finally {
      setAjusteLoading(false);
    }
  };

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">
          <i className="bi bi-wallet2 me-2" style={{ color: 'var(--fg-green)', fontSize: '1.2rem' }} />
          Wallets MVZ
        </h1>
        <p className="page-subtitle">Consulta y gestiona la billetera de cualquier usuario MVZ</p>
      </div>

      {successMsg && (
        <div className="alert alert-success d-flex align-items-center gap-2 mb-3" style={{ borderRadius: 10 }}>
          <i className="bi bi-check-circle-fill" /> {successMsg}
        </div>
      )}

      {/* Buscador */}
      <div className="fg-card mb-4">
        <div className="fg-card-header">
          <div className="fg-card-title">
            <i className="bi bi-search me-2" style={{ color: 'var(--fg-green)' }} />
            Buscar wallet por MVZ ID
          </div>
        </div>
        <div style={{ padding: '20px 22px' }}>
          <form onSubmit={handleBuscar} className="d-flex gap-2 flex-wrap">
            <input
              type="text"
              className="form-control"
              style={{ maxWidth: 380, borderRadius: 8, fontSize: '0.875rem' }}
              placeholder="Pega el UUID del MVZ aquí..."
              value={mvzId}
              onChange={(e) => setMvzId(e.target.value)}
            />
            <button className="btn-fg-primary" type="submit" disabled={loading || !mvzId.trim()}>
              {loading ? <span className="spinner-border spinner-border-sm" /> : <><i className="bi bi-search" /> Buscar</>}
            </button>
          </form>
          {error && (
            <div className="alert alert-danger mt-3 mb-0 py-2" style={{ borderRadius: 8, fontSize: '0.85rem' }}>
              <i className="bi bi-exclamation-triangle me-2" />{error}
            </div>
          )}
        </div>
      </div>

      {/* Resultado */}
      {wallet && (
        <div className="row g-3">
          {/* Info principal */}
          <div className="col-12 col-lg-6">
            <div className="fg-card h-100">
              <div className="fg-card-header">
                <div className="fg-card-title">
                  <i className="bi bi-person-circle me-2" style={{ color: 'var(--fg-green)' }} />
                  {wallet.mvzNombre}
                </div>
                <span className={`badge-estado badge-${wallet.estadoWallet}`}>
                  {wallet.estadoWallet}
                </span>
              </div>
              <div style={{ padding: '20px 22px' }}>
                <div className="row g-3">
                  {[
                    { label: 'Saldo disponible', value: fmt(wallet.saldoDisponible), color: 'var(--fg-green)' },
                    { label: 'Saldo pendiente', value: fmt(wallet.saldoPendiente), color: 'var(--fg-blue)' },
                    { label: 'Deuda comisiones', value: wallet.saldoNegativo > 0 ? `-${fmt(wallet.saldoNegativo)}` : '$0', color: wallet.saldoNegativo > 0 ? 'var(--fg-red)' : 'var(--fg-muted)' },
                    { label: 'Total ganado', value: fmt(wallet.totalGanado), color: 'var(--fg-green)' },
                    { label: 'Total retirado', value: fmt(wallet.totalRetirado), color: 'var(--fg-text)' },
                    { label: 'Comisiones pendientes', value: fmt(wallet.totalComisionesPendientes), color: 'var(--fg-orange)' },
                  ].map((item) => (
                    <div className="col-6" key={item.label}>
                      <div style={{ background: '#f8fafb', borderRadius: 10, padding: '12px 14px' }}>
                        <div style={{ fontSize: '0.72rem', color: 'var(--fg-muted)', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase' }}>
                          {item.label}
                        </div>
                        <div style={{ fontSize: '1.1rem', fontWeight: 800, color: item.color }}>
                          {item.value}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-3" style={{ fontSize: '0.8rem', color: 'var(--fg-muted)' }}>
                  <i className="bi bi-hash me-1" />
                  Wallet ID: <code style={{ fontSize: '0.75rem' }}>{wallet.walletId}</code>
                </div>
                {wallet.cantidadComisionesPendientes > 0 && (
                  <div className="alert alert-warning mt-3 mb-0 py-2 d-flex align-items-center gap-2" style={{ borderRadius: 8, fontSize: '0.82rem' }}>
                    <i className="bi bi-exclamation-triangle-fill" />
                    {wallet.cantidadComisionesPendientes} comisión(es) pendientes de pago
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Acciones admin */}
          <div className="col-12 col-lg-6">
            <div className="fg-card h-100">
              <div className="fg-card-header">
                <div className="fg-card-title">
                  <i className="bi bi-tools me-2" style={{ color: 'var(--fg-orange)' }} />
                  Acciones administrativas
                </div>
              </div>
              <div style={{ padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ background: '#fffbf5', border: '1px solid rgba(249,129,21,0.2)', borderRadius: 10, padding: '14px 16px', fontSize: '0.82rem' }}>
                  <i className="bi bi-info-circle me-2" style={{ color: 'var(--fg-orange)' }} />
                  Los ajustes manuales quedan registrados en el historial de movimientos de la wallet.
                  Úsalos solo para correcciones autorizadas.
                </div>
                <button
                  className="btn-fg-outline"
                  style={{ justifyContent: 'flex-start', padding: '12px 16px' }}
                  onClick={() => setAjusteModal(true)}
                >
                  <i className="bi bi-pencil-square" />
                  Ajuste manual de saldo
                </button>
                <button
                  className="btn-fg-outline"
                  style={{ justifyContent: 'flex-start', padding: '12px 16px' }}
                  onClick={() => { setMvzId(wallet.mvzId); handleBuscar({ preventDefault: () => {} } as any); }}
                >
                  <i className="bi bi-arrow-clockwise" />
                  Actualizar datos
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal ajuste */}
      {ajusteModal && wallet && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setAjusteModal(false)}>
          <div className="modal-box">
            <div className="modal-title">
              <i className="bi bi-pencil-square" style={{ color: 'var(--fg-orange)' }} />
              Ajuste manual · {wallet.mvzNombre}
            </div>
            <div className="alert alert-warning py-2 mb-3" style={{ borderRadius: 8, fontSize: '0.82rem' }}>
              <i className="bi bi-exclamation-triangle me-2" />
              Usa valores positivos para acreditar y negativos para debitar.
            </div>
            <div className="mb-3">
              <label className="form-label fw-semibold" style={{ fontSize: '0.85rem' }}>Monto (COP)</label>
              <input
                type="number"
                className="form-control"
                placeholder="Ej: 50000 o -20000"
                value={ajusteMonto}
                onChange={(e) => setAjusteMonto(e.target.value)}
                style={{ borderRadius: 8 }}
              />
            </div>
            <div className="mb-2">
              <label className="form-label fw-semibold" style={{ fontSize: '0.85rem' }}>Descripción *</label>
              <textarea
                className="form-control"
                rows={2}
                placeholder="Motivo del ajuste..."
                value={ajusteDesc}
                onChange={(e) => setAjusteDesc(e.target.value)}
                style={{ borderRadius: 8 }}
              />
            </div>
            <div className="modal-actions">
              <button className="btn btn-outline-secondary btn-sm" onClick={() => setAjusteModal(false)}>Cancelar</button>
              <button
                className="btn-fg-primary"
                onClick={handleAjuste}
                disabled={ajusteLoading || !ajusteMonto || !ajusteDesc.trim()}
              >
                {ajusteLoading ? <span className="spinner-border spinner-border-sm" /> : <><i className="bi bi-check-lg" /> Aplicar ajuste</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
